import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ordersApi } from '../api';
import toast from 'react-hot-toast';
import { useReactToPrint } from "react-to-print";
import Receipt from "../components/common/Receipt";
import { useRef } from "react";
const STATUS_FLOW = ['pending', 'stitching', 'qc', 'ready', 'delivered'];

export default function OrdersDetails() {
  const { id } = useParams();
  const receiptRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: receiptRef
  });
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await ordersApi.getById(id);
      setOrder(res.data.data);
    } catch (err) {
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status) => {
    setUpdating(true);
    try {
      await ordersApi.updateStatus(id, status);
      toast.success('Status updated');
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const receiveBalance = async () => {
    const amount = Number(balanceAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter valid amount');
      return;
    }
    const currentAdvance = Number(order.advancePayment || order.advance_payment || 0);
    const newTotal = currentAdvance + amount;
    setUpdating(true);
    try {
      await ordersApi.update(id, { advancePayment: newTotal });
      toast.success(`₹${amount} received! Total paid: ₹${newTotal}`);
      setShowBalanceModal(false);
      setBalanceAmount('');
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update payment');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <p>Order not found</p>
          <Link to="/orders" className="btn btn-primary">Back to Orders</Link>
        </div>
      </div>
    );
  }

  const currentStatusIndex = STATUS_FLOW.indexOf(order.status);
  const nextStatus = STATUS_FLOW[currentStatusIndex + 1];
  const prevStatus = STATUS_FLOW[currentStatusIndex - 1];

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };


  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/orders" className="btn btn-secondary btn-sm">← Back</Link>
          <div>
            <h1 className="page-title" style={{ marginBottom: 4 }}>Order {order.orderNumber}</h1>
            <span className={`badge badge-${order.status}`}>{order.status}</span>
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handlePrint}>
          Print Receipt
        </button>
        <div className="flex gap-2">
          {prevStatus && order.status !== 'cancelled' && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => updateStatus(prevStatus)}
              disabled={updating}
            >
              ← {prevStatus}
            </button>
          )}
          {nextStatus && order.status !== 'cancelled' && (
            <button
              className="btn btn-success btn-sm"
              onClick={() => updateStatus(nextStatus)}
              disabled={updating}
            >
              {nextStatus} →
            </button>
          )}
          {order.status !== 'cancelled' && order.status !== 'delivered' && (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => updateStatus('cancelled')}
              disabled={updating}
            >
              Cancel Order
            </button>
          )}
          {Number(order.advancePayment || order.advance_payment || 0) < Number(order.totalSellingPrice) && order.status !== 'cancelled' && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowBalanceModal(true)}
              disabled={updating}
            >
              Receive Balance
            </button>
          )}
        </div>
      </div>

      <div className="grid-2 " style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        <div className="flex flex-col gap-4 ">
          {/* Order Info Card */}
          <div className="card p-2">
            <h3 className="card-title">Order Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Order Number</span>
                <span className="info-value mono">{order.orderNumber}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className={`info-value badge badge-${order.status}`}>{order.status}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created Date</span>
                <span className="info-value">{formatDate(order.createdAt)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Due Date</span>
                <span className="info-value">{formatDate(order.dueDate)}</span>
              </div>
              {order.deliveredAt && (
                <div className="info-item">
                  <span className="info-label">Delivered Date</span>
                  <span className="info-value">{formatDate(order.deliveredAt)}</span>
                </div>
              )}
              {order.creator && (
                <div className="info-item">
                  <span className="info-label">Created By</span>
                  <span className="info-value">{order.creator.name}</span>
                </div>
              )}
            </div>
            {order.notes && (
              <div className="mt-4">
                <span className="info-label">Notes</span>
                <p className="info-value text-sm" style={{ marginTop: 4 }}>{order.notes}</p>
              </div>
            )}
          </div>

          <div className="card p-2">
            <h3 className="card-title">Customer Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Name</span>
                <span className="info-value font-semibold">{order.customer?.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value">{order.customer?.phone}</span>
              </div>
              {order.customer?.email && (
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{order.customer?.email}</span>
                </div>
              )}
              {order.customer?.address && (
                <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                  <span className="info-label">Address</span>
                  <span className="info-value">{order.customer?.address}</span>
                </div>
              )}
            </div>
          </div>

         {/* Order Summary Card */}
          <div className="card p-2">
            <h3 className="card-title">Order Summary</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Total Items</span>
                <span>{order.items?.length || 0}</span>
              </div>
              <div className="summary-row">
                <span>Total Cost</span>
                <span>₹{Number(order.totalCost).toLocaleString('en-IN')}</span>
              </div>
              <div className="summary-row">
                <span>Selling Price</span>
                <span className="text-success font-semibold">₹{Number(order.totalSellingPrice).toLocaleString('en-IN')}</span>
              </div>
              {Number(order.advancePayment || order.advance_payment || 0) > 0 && (
                <>
                  <div className="summary-row">
                    <span>Advance Paid</span>
                    <span className="text-primary font-semibold">₹{Number(order.advancePayment || order.advance_payment || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="summary-row">
                    <span>Balance</span>
                    <span className="text-danger font-semibold">₹{(Number(order.totalSellingPrice) - Number(order.advancePayment || order.advance_payment || 0)).toLocaleString('en-IN')}</span>
                  </div>
                </>
              )}
              <div className="summary-row total">
                <span>Profit</span>
                <span className={`font-bold ${Number(order.profit) >= 0 ? 'text-success' : 'text-danger'}`}>
                  ₹{Number(order.profit).toLocaleString('en-IN')}
                </span>
              </div>
              {order.profitMargin && (
                <div className="summary-row">
                  <span>Profit Margin</span>
                  <span>{Number(order.profitMargin).toFixed(2)}%</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Order Items Card */}
          <div className="card p-2">
            <h3 className="card-title">Order Items ({order.items?.length || 0})</h3>
            {order.items && order.items.length > 0 ? (
              <div className="items-list">
                {order.items.map((item, idx) => (
                  <div key={item.id} className="order-item-card">
                    <div className="order-item-header">
                      <span className="item-garment">{item.garmentType}</span>
                      <span className={`badge badge-${item.status}`}>{item.status}</span>
                    </div>
                    <div className="order-item-details">
                      <div className="detail-row">
                        <span className="detail-label">Cloth:</span>
                        <span className="detail-value">
                          {item.inventory?.lot?.lotNumber} - {item.inventory?.clothType?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Qty Used:</span>
                        <span className="detail-value">{item.clothQuantityUsed} {item.inventory?.lot?.unit || 'm'}</span>
                      </div>
                      {item.template && (
                        <div className="detail-row">
                          <span className="detail-label">Accessories:</span>
                          <span className="detail-value">{item.template.name}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">Cost:</span>
                        <span className="detail-value">₹{Number(item.totalCost).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Price:</span>
                        <span className="detail-value text-success font-semibold">₹{Number(item.sellingPrice).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Profit:</span>
                        <span className={`detail-value font-semibold ${Number(item.profit) >= 0 ? 'text-success' : 'text-danger'}`}>
                          ₹{Number(item.profit).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>

                    {/* Tailor Assignments */}
                    {item.assignments && item.assignments.length > 0 && (
                      <div className="item-assignments">
                        <span className="detail-label">Assigned Tailors:</span>
                        <div className="assignments-list">
                          {item.assignments.map((assign) => (
                            <div key={assign.id} className="assignment-item">
                              <span className="tailor-name">{assign.tailor?.name}</span>
                              <span className={`badge badge-${assign.status}`}>{assign.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Item-specific Measurements */}
                    {item.measurements && (
                      <div className="item-measurements">
                        <span className="detail-label">{item.garmentType} Measurements:</span>
                        <div className="measurements-grid">
                          {Object.entries(item.measurements).map(([key, value]) => (
                            value && (
                              <div key={key} className="measurement-chip">
                                <span className="measurement-key">{key.replace('_', ' ')}:</span>
                                <span className="measurement-val">{value}</span>
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No items in this order</p>
              </div>
            )}
          </div>

          
        </div>
      </div>

      <style>{`
        .grid-2 {
          display: grid;
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
        .flex {
          display: flex;
        }
        .flex-col {
          flex-direction: column;
        }
        .items-center {
          align-items: center;
        }
        .gap-2 {
          gap: 8px;
        }
        .gap-4 {
          gap: 16px;
        }
        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .info-label {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .info-value {
          font-size: 0.9rem;
          color: var(--text-primary);
        }
        .measurement-section {
          margin-bottom: 16px;
        }
        .measurement-section:last-child {
          margin-bottom: 0;
        }
        .measurement-type {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--accent);
          margin-bottom: 8px;
        }
        .measurement-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .measurement-item {
          display: flex;
          flex-direction: column;
          background: var(--bg-secondary);
          padding: 8px;
          border-radius: 6px;
        }
        .measurement-label {
          font-size: 0.7rem;
          color: var(--text-muted);
          text-transform: capitalize;
        }
        .measurement-value {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-primary);
        }
        .items-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .order-item-card {
          background: var(--bg-secondary);
          border-radius: 8px;
          padding: 12px;
        }
        .order-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
        }
        .item-garment {
          font-weight: 600;
          color: var(--text-primary);
        }
        .order-item-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
        }
        .detail-label {
          color: var(--text-muted);
        }
        .detail-value {
          color: var(--text-primary);
        }
        .item-assignments {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid var(--border-color);
        }
        .assignments-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-top: 4px;
        }
        .assignment-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.8rem;
        }
        .tailor-name {
          color: var(--text-primary);
        }
        .summary-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color);
        }
        .summary-row:last-child {
          border-bottom: none;
        }
        .summary-row.total {
          font-size: 1rem;
          padding-top: 12px;
          margin-top: 4px;
          border-top: 2px solid var(--border-color);
        }
        .badge-pending { background: #fef3c7; color: #92400e; }
        .badge-stitching { background: #dbeafe; color: #1e40af; }
        .badge-qc { background: #e0e7ff; color: #3730a3; }
        .badge-ready { background: #d1fae5; color: #065f46; }
        .badge-delivered { background: #dcfce7; color: #166534; }
        .badge-cancelled { background: #fee2e2; color: #991b1b; }
        .badge-in_progress { background: #dbeafe; color: #1e40af; }
        .badge-done { background: #d1fae5; color: #065f46; }
        .item-measurements {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid var(--border-color);
        }
        .measurements-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }
        .measurement-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: var(--bg-primary);
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 0.8rem;
        }
        .measurement-key {
          color: var(--text-muted);
          text-transform: capitalize;
        }
        .measurement-val {
          font-weight: 600;
          color: var(--accent);
        }
      `}</style>
      <div style={{ display: "none" }}>
        <Receipt ref={receiptRef} order={order} />
      </div>

      {showBalanceModal && order && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowBalanceModal(false)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h3>Receive Balance Payment</h3>
              <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setShowBalanceModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="summary-table mb-4">
                <div className="summary-row">
                  <span>Total Amount</span>
                  <span>₹{Number(order.totalSellingPrice).toLocaleString('en-IN')}</span>
                </div>
                <div className="summary-row">
                  <span>Already Paid</span>
                  <span>₹{Number(order.advancePayment || order.advance_payment || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="summary-row total">
                  <span>Balance Due</span>
                  <span className="text-danger font-bold">₹{(Number(order.totalSellingPrice) - Number(order.advancePayment || order.advance_payment || 0)).toLocaleString('en-IN')}</span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Amount Received (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={balanceAmount}
                  onChange={(e) => setBalanceAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBalanceModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={receiveBalance} disabled={updating || !balanceAmount}>
                {updating ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
